import {useCallback} from "react";
import log from "loglevel";
import {Intent} from "@blueprintjs/core";
import {SnoozeIndicatorEntityType, useSnoozeIndicatorMutation} from "../generated/graphql";
import {showToastWithTitle} from "../utils/toasters/ToasterWithTitle";
import {SnoozeTarget, snoozeTargetsIndicators} from "../components/manifest/details/utils/ManifestDetails.utils";

export const SNOOZE_SUCCESS_TITLE = "Snooze Submitted";
export const SNOOZE_SUCCESS_MESSAGE = (stops: SnoozeTarget[], minutes: number) =>
  `We’ve submitted the request to snooze ${snoozeTargetsIndicators(stops)} for ${minutes} minutes.`;
export const SNOOZE_FAILURE_TITLE = "Snooze Failed";
export const SNOOZE_FAILURE_MESSAGE = (stops: SnoozeTarget[]) =>
  `Failed to snooze ${snoozeTargetsIndicators(stops)}. Please try again.`;

export interface UseSnoozeIndicatorParams {
  /** The open drawer's manifest, sent so the backend can tell open drawers to refetch. */
  manifestDriverId: number;
  /** Called only after the mutation resolves, with the end of the snooze as seen by this client. */
  onSnoozed: (jobStopIds: number[], snoozeUntilIso: string) => void;
}

// DP-3070: snoozes the colour indicator of one or more stops from Manifest Details, in one request.
//
// The mutation only hands the request to EventBridge; the backend applies it asynchronously. So a
// resolved mutation means "submitted" (same wording as the manifest vehicle/status updates), and a
// backend failure after that cannot be shown here. A rejected mutation (network, AppSync or a
// GraphQL error, which Apollo's default errorPolicy turns into a rejection) shows the failure toast
// and leaves the stops untouched.
export const useSnoozeIndicator = ({manifestDriverId, onSnoozed}: UseSnoozeIndicatorParams) => {
  const [snoozeIndicatorMutation] = useSnoozeIndicatorMutation();

  const snooze = useCallback(
    async (stops: SnoozeTarget[], minutes: number) => {
      const ids = stops.map((stop) => stop.jobStopId);
      try {
        await snoozeIndicatorMutation({
          variables: {entityType: SnoozeIndicatorEntityType.Stop, ids, minutes, manifestDriverId}
        });
        onSnoozed(ids, new Date(Date.now() + minutes * 60_000).toISOString());
        showToastWithTitle({
          intent: Intent.SUCCESS,
          title: SNOOZE_SUCCESS_TITLE,
          message: SNOOZE_SUCCESS_MESSAGE(stops, minutes),
          delay: 3000
        });
      } catch (error) {
        log.error("snoozeIndicator failed", error);
        showToastWithTitle({
          intent: Intent.WARNING,
          title: SNOOZE_FAILURE_TITLE,
          message: SNOOZE_FAILURE_MESSAGE(stops)
        });
      }
    },
    [snoozeIndicatorMutation, manifestDriverId, onSnoozed]
  );

  return {snooze};
};
